<template>
  <div class="chat">
    <header class="flex flex-col justify-between px-5 w-full bg-black">
      <div
        class="flex flex-col justify-center py-3 w-full text-lg font-bold leading-6 text-center text-white whitespace-nowrap bg-zinc-800"
      >
        <div class="relative flex items-center">
          <div class="menu-icon absolute">
            <ShowMenu @click="showMenu" />
          </div>
          <div class="flex-1">
            {{ $t('chat.title') }}
          </div>
        </div>
      </div>
    </header>

    <section
      class="self-center mt-7 text-base leading-6 text-white flex-1 flex flex-col w-full overflow-y-auto"
    >
      <div ref="chatContainer" class="body-container h-full overflow-y-auto">
        <!-- <h2 class="self-center mt-4 text-lg font-bold leading-6 text-white">Today</h2> -->
        <div v-for="message in processedMessages" :key="message.msg_id">
          <MyChatMessage v-if="message.role === MESSAGE_FROM.user" :message="message" />
          <AIChatMessage
            v-else-if="message.role === MESSAGE_FROM.assistant"
            :message="message"
            @done-typing="handleQueue(message)"
            @begin-scan="goToScanBody"
            @upload-image="uploadImage"
            @try-on="tryOnGarment"
            @select-size="tryOnGarment"
            @on-image-click="openGalleryLightbox"
            @learn-more="learnMore"
          />
        </div>
        <div v-if="typing.isShow" class="typing-container overflow-hidden">
          <div v-if="typing.text" class="mx-4">
            {{ typing.text }}
          </div>
          <Typing />
        </div>
      </div>
    </section>
    <div class="flex flex-col pb-3 w-full bg-black mt-[544px]">
      <div
        class="flex flex-nowrap gap-2 py-2 px-4 w-full text-xs leading-6 text-white overflow-x-auto"
      >
        <button
          class="shortcut-button flex items-center px-3 py-1 border border-white border-solid bg-black bg-opacity-50 rounded-[38px] whitespace-nowrap flex-shrink-0"
        >
          <span>
            {{ $t('chat.my_body_report') }}
          </span>
          <img src="@/assets/common/open-arrow.svg" class="ml-1" />
        </button>
        <button
          class="shortcut-button flex items-center px-3 py-1 border border-white border-solid bg-black bg-opacity-50 rounded-[38px] whitespace-nowrap flex-shrink-0"
          @click="goToScanBody"
        >
          <span>
            {{ $t('chat.body_scan') }}
          </span>
          <img src="@/assets/common/open-arrow.svg" class="ml-1" />
        </button>
        <button
          class="shortcut-button flex items-center px-3 py-1 border border-white border-solid bg-black bg-opacity-50 rounded-[38px] whitespace-nowrap flex-shrink-0"
        >
          <span>
            {{ $t('chat.suggest_clothing') }}
          </span>
          <img src="@/assets/common/open-arrow.svg" class="ml-1" />
        </button>
      </div>
      <div class="flex gap-2 justify-center mx-4 mt-3">
        <label for="styleQuestion" class="sr-only">{{ $t('chat.placeholder') }}</label>

        <textarea
          ref="textarea"
          v-model="question"
          rows="1"
          maxlength="380"
          class="question-textarea"
          placeholder="Ask me any style question"
          aria-label="Ask me any style question"
          @input="onInput"
        />

        <button
          class="send-button flex justify-center items-center p-4 w-12 h-12 rounded mt-auto"
          :disabled="isDisabled"
          @click="sendQuestion"
        >
          <img
            loading="lazy"
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/49d5a5a6b7834bba431f8bdcd4f1077d7cf39d268f2eb4f25a15d0c41467c41e?apiKey=bacbc2ba64a2493ba06c07076d526b1a"
            class="w-4 aspect-square"
            alt="Send icon"
          />
        </button>
      </div>
    </div>
    <transition name="fade">
      <GalleryLightbox
        :show="vtoLightbox.isShow"
        :imgs="vtoImgs"
        :currentId.sync="vtoLightbox.currentId"
        @close="closeGalleryLightbox"
      />
    </transition>
    <BodyScan :show="isShowBodyScan" @cancel="isShowBodyScan = false" @done="bodyScanCompleted" />
  </div>
</template>

<script>
import { ChatWebSocket, CHAT_SOCKET_EVENTS } from '@/modules/bodyai/index';
import $api from '@/modules/request';
import ShowMenu from '@/components/common/ShowMenu.vue';
import showAppMenu from '@/mixins/show-app-menu';
import * as ExifReader from 'exifreader';
// import ScanProcess from '@/modules/scan-process';
import { v4 as uuidv4 } from 'uuid';
import { MESSAGE_FROM, transformResponseToMessage } from './messages/index';
import MyChatMessage from './messages/components/MyChatMessage/MyChatMessage.vue';
import AIChatMessage from './messages/components/AIChatMessage/AIChatMessage.vue';
import Typing from './messages/components/Typing.vue';
import GalleryLightbox from './messages/components/Lightbox/GalleryLightbox.vue';
import BodyScan from './BodyScan/BodyScan.vue';
import {
  MESSAGE_TYPE,
  CARD_TYPE,
  ACTION_TYPE,
  // BODY_AI_SCANNER_PAYLOAD,
  BODY_SHAPE,
} from './messages/constants/index';

let observer;
const generateMessage = ({ role, content, kind = 'text' }) => {
  const msg = {
    role,
    content,
    kind,
  };
  const message = {
    command: 'message',
    identifier: JSON.stringify({ channel: 'ConversationChannel' }),
    data: JSON.stringify({
      action: 'chat',
      msg,
    }),
  };
  return { json: JSON.stringify(message), msg };
};

export default {
  name: 'Chat',
  components: {
    MyChatMessage,
    AIChatMessage,
    Typing,
    // Menu,
    ShowMenu,
    GalleryLightbox,
    BodyScan,
  },
  mixins: [showAppMenu],
  data() {
    return {
      MESSAGE_FROM,

      /**
       * @type {ChatWebSocket}
       */
      socket: null,

      question: '',
      messages: [],
      isFetched: false,
      isSocketConnected: false,
      isAiDoneTalking: true,
      // messageQueue: [],
      isScrolledToBottom: true,
      typing: {
        isShow: false,
        text: '',
      },
      isScanCompleted: false,
      conversationHistory: [],
      vtoLightbox: {
        isShow: false,
        currentId: '',
      },
      isShowBodyScan: false,
      // tryOnGarmentId: '',
    };
  },
  computed: {
    lastMessage() {
      return this.messages[this.message.length - 1];
    },
    isDisabled() {
      return (
        !this.isFetched ||
        !this.isSocketConnected ||
        !this.question ||
        !this.isAiDoneTalking ||
        this.typing.isShow
      );
    },
    isSocketConnectedAndFetched() {
      return this.isSocketConnected && this.isFetched;
    },
    vtoImgs() {
      return this.messages.reduce((acc, cur) => {
        if (cur.kind === MESSAGE_TYPE.cards && cur.content?.[0]?.kind === CARD_TYPE.image) {
          const content = cur.content[0];
          const isVto = !cur.content[0].action;
          if (isVto) {
            acc.push({
              id: cur.msg_id,
              name: this.$t('chat.size', { msg: content.data.garment_size }),
              src: content.image,
            });
          }
        }
        return acc;
      }, []);
    },
    processedMessages() {
      return this.messages.reduce((acc, message) => {
        const { role, content, kind } = message;
        if (role === MESSAGE_FROM.system && kind === MESSAGE_TYPE.action) {
          if (content.action === ACTION_TYPE.vto) {
            const size = content.data.garment_size;
            acc.push({
              ...message,
              role: MESSAGE_FROM.user,
              content: size ? this.$t('chat.size', { msg: size }) : this.$t('chat.try_on'),
              doneTyping: true,
            });
            return acc;
          }
          if (content.action === ACTION_TYPE.explainBodyShape) {
            acc.push({
              ...message,
              role: MESSAGE_FROM.user,
              content: this.$t('chat.learn_more'),
              doneTyping: true,
            });
            return acc;
          }
          return acc;
        }
        acc.push({ ...message, doneTyping: true });
        return acc;
      }, []);
    },
  },
  async mounted() {
    // this.isScanCompleted = !!this.$route.query.scan_completed;
    // // remove scan_completed query param
    // if (this.isScanCompleted) {
    //   const newQuery = { ...this.$route.query };
    //   delete newQuery.scan_completed;
    //   this.$router.replace({ query: newQuery });
    // }

    const unwatch = this.$watch('isSocketConnectedAndFetched', () => {
      if (this.conversationHistory.length) {
        this.messages = this.conversationHistory.map(message => ({
          ...message.message,
          doneTyping: true,
        }));
      } else {
        this.isAiDoneTalking = false;
        this.typing.isShow = true;
        this.promptAiIntroduction();
      }

      // if (this.isScanCompleted) {
      // this.typing.text = this.$t('chat.creating_analysis');
      // this.typing.isShow = true;
      // const { json } = generateMessage({
      //   role: MESSAGE_FROM.system,
      //   content: {
      //     text: '',
      //     action: 'body_scan_finished',
      //     data: {},
      //   },
      //   kind: MESSAGE_TYPE.action,
      // });
      // this.socket.send(json);
      // }
      unwatch();
    });

    try {
      await this.initChatSocket();
      await this.getAllMessages();
      this.isFetched = true;

      this.autoGrowHeight();

      this.$refs.chatContainer.addEventListener('scroll', this.onChatScroll);
      this.initObserver();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`mounted err`, error);
    }
  },
  beforeDestroy() {
    this.$refs.chatContainer.removeEventListener('scroll', this.onChatScroll);
    if (observer) {
      observer.disconnect();
    }
  },
  methods: {
    autoGrowHeight(element = this.$refs.textarea) {
      /* eslint-disable no-param-reassign */
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
      /* eslint-enable no-param-reassign */
    },

    onInput(e) {
      this.autoGrowHeight(e.target);
    },

    async sendQuestion() {
      try {
        const { json, msg } = generateMessage({
          role: MESSAGE_FROM.user,
          content: this.question,
          kind: 'text',
        });
        this.messages.push({ ...msg, doneTyping: true });
        this.scrollToBottom();
        this.socket.send(json);
        this.question = '';
        this.typing.isShow = true;
        this.isAiDoneTalking = false;

        await this.$nextTick();
        this.autoGrowHeight();
      } catch (error) {
        console.error(`sendQuestion error: `, error);
      }
    },
    async goToScanBody() {
      this.isShowBodyScan = true;
      // try {
      //   this.$events.startLoading(true);
      //   // await ScanProcess.cancel();
      //   // await ScanProcess.initial(BODY_AI_SCANNER_PAYLOAD);
      //   this.$events.stopLoading();
      //   this.$events.goto('MobileScan2/UserProfile');
      // } catch (err) {
      //   console.log('init bodyAI scanner err', err);
      //   this.$events.stopLoading();
      // }
    },
    bodyScanCompleted() {
      this.scrollToBottom();
      this.isShowBodyScan = false;
      this.typing.text = this.$t('chat.creating_analysis');
      this.typing.isShow = true;
      const { json } = generateMessage({
        role: MESSAGE_FROM.system,
        content: {
          text: '',
          action: 'body_scan_finished',
          data: {},
        },
        kind: MESSAGE_TYPE.action,
      });
      this.socket.send(json);
      // do some other stuff
    },
    onSocketNewMessage(data) {
      if (!data) return;

      const message = transformResponseToMessage(data);
      if (!message) return;

      const existingMessage = this.messages.find(msg => msg.msg_id === message.msg_id);
      // const existingMessage =
      //   this.messages.find(msg => msg.msg_id === message.msg_id) ||
      //   this.messageQueue.find(msg => msg.msg_id === message.msg_id);
      if (!existingMessage) {
        // only text messages have typing animation
        // message.doneTyping = message.kind !== MESSAGE_TYPE.text;

        // const pushToMessages =
        //   !this.messages.length ||
        //   (!this.messageQueue.length && this.messages[this.messages.length - 1].doneTyping);
        // if (pushToMessages) {
        //   this.messages.push(message);
        // } else {
        //   this.messageQueue.push(message);
        // }
        this.messages.push(message);
      } else {
        existingMessage.content += message.content;
        existingMessage.end = message.end;
      }
      this.resetTyping();
      this.isAiDoneTalking = message.end;

      // const isIncomingMessageVto =
      //   message.kind === MESSAGE_TYPE.image && message.content[0] && !message.content.action;
      // if (isIncomingMessageVto) {
      //   this.tryOnGarmentId = '';
      // }
    },
    handleQueue(message) {
      // eslint-disable-next-line no-param-reassign
      // message.doneTyping = true;
      // if (this.messageQueue.length) {
      //   this.messages.push(this.messageQueue.shift());
      // }
    },
    initObserver() {
      const { chatContainer } = this.$refs;
      const observerCb = mutationList => {
        if (!this.isScrolledToBottom) return;

        /* eslint-disable no-restricted-syntax */
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        }
        /* eslint-enable no-restricted-syntax */
      };

      observer = new MutationObserver(observerCb);
      observer.observe(chatContainer, { childList: true, subtree: true });
    },
    onChatScroll() {
      const { chatContainer } = this.$refs;
      const { height } = chatContainer.getBoundingClientRect();
      this.isScrolledToBottom = chatContainer.scrollHeight - height - chatContainer.scrollTop <= 20;
    },
    scrollToBottom() {
      const { chatContainer } = this.$refs;
      const { height } = chatContainer.getBoundingClientRect();
      chatContainer.scrollTop = chatContainer.scrollHeight - height;
      this.isScrolledToBottom = true;
    },
    promptAiIntroduction() {
      const { json } = generateMessage({
        role: MESSAGE_FROM.system,
        content: this.$t('chat.introduce_yourself'),
        kind: MESSAGE_TYPE.text,
      });
      this.socket.send(json);
    },
    async uploadImage(e) {
      if (this.typing.isShow || !this.isAiDoneTalking) return;
      this.typing.isShow = true;

      try {
        const image = e.target.files[0];
        const uprightImage = await this.getUprightImage(image);
        const formData = new FormData();
        formData.append('image', uprightImage);
        await $api.user.post('/api/v1/vto_source_images', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // if (this.tryOnGarmentId) {
        //   this.resetTyping();
        //   this.tryOnGarment(
        //     {
        //       garment_id: this.tryOnGarmentId,
        //     },
        //     true
        //   );
        // } else {
        const { json } = generateMessage({
          role: MESSAGE_FROM.system,
          content: this.$t('chat.try_on_prompt'),
          kind: MESSAGE_TYPE.text,
        });
        this.socket.send(json);
        // }
      } catch (err) {
        console.log('upload err', err);
        this.resetTyping();
      } finally {
        e.target.value = '';
      }
    },
    getUprightImage(file) {
      return new Promise(resolve => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = e => {
          img.src = e.target.result;

          img.onload = () => {
            ExifReader.load(file).then(data => {
              const orientation = data.Orientation;
              if (!orientation) {
                resolve(file);
                return;
              }

              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');

              const { width, height } = img;
              if (orientation >= 5 && orientation <= 8) {
                canvas.width = height;
                canvas.height = width;
              } else {
                canvas.width = width;
                canvas.height = height;
              }

              switch (orientation) {
                case 2:
                  ctx.transform(-1, 0, 0, 1, width, 0);
                  break; // Flip horizontal
                case 3:
                  ctx.transform(-1, 0, 0, -1, width, height);
                  break; // Rotate 180°
                case 4:
                  ctx.transform(1, 0, 0, -1, 0, height);
                  break; // Flip vertical
                case 5:
                  ctx.transform(0, 1, 1, 0, 0, 0);
                  break; // Transpose
                case 6:
                  ctx.transform(0, 1, -1, 0, height, 0);
                  break; // Rotate 90° CW
                case 7:
                  ctx.transform(0, -1, -1, 0, height, width);
                  break; // Transverse
                case 8:
                  ctx.transform(0, -1, 1, 0, 0, width);
                  break; // Rotate 90° CCW
                default:
                  break; // Normal
              }

              ctx.drawImage(img, 0, 0);

              // Convert canvas to Blob
              canvas.toBlob(blob => {
                const processedFile = new File([blob], file.name, {
                  type: file.type,
                });
                resolve(processedFile);
              }, file.type);
            });
          };
        };

        reader.readAsDataURL(file);
      });
    },
    /* eslint-disable camelcase */
    async tryOnGarment({ garment_id, garment_size = null }, isInBackground = false) {
      if (this.typing.isShow || !this.isAiDoneTalking) return;

      // this.tryOnGarmentId = garment_id;
      /* eslint-enable camelcase */
      this.typing.text = this.$t('chat.simulating_try_on');
      this.typing.isShow = true;

      const { json, msg } = generateMessage({
        role: MESSAGE_FROM.system,
        kind: MESSAGE_TYPE.action,
        content: {
          text: '',
          action: ACTION_TYPE.vto,
          data: {
            garment_id,
            garment_size,
          },
        },
      });

      // if (!isInBackground) {
      this.messages.push({ ...msg, msg_id: uuidv4() });
      // }
      this.socket.send(json);
    },
    resetTyping() {
      this.typing.text = '';
      this.typing.isShow = false;
    },
    openGalleryLightbox(id) {
      this.vtoLightbox.currentId = id;
      this.vtoLightbox.isShow = true;
    },
    closeGalleryLightbox() {
      this.vtoLightbox.isShow = false;
    },
    async getAllMessages() {
      return new Promise(resolve => {
        const allMessages = [];
        const getMessages = async (offset = 0) => {
          const {
            data: { messages, offset: dataOffset, total: dataTotal },
          } = await $api.user.get(`api/v1/conversations/0?limit=100&offset=${offset}`);
          allMessages.push(...messages);
          if (!messages.length) {
            resolve();
          } else if (dataTotal > dataOffset + 100) {
            await getMessages(dataOffset + 100);
          } else {
            this.conversationHistory = allMessages;
            resolve();
          }
        };

        getMessages();
      });
    },
    learnMore(type) {
      if (this.typing.isShow || !this.isAiDoneTalking) return;

      const content = this.$t(`chat.learn_more_about_body_shape`, {
        msg: this.$t(`chat.${BODY_SHAPE[type]}`),
      });
      const { json, msg } = generateMessage({
        role: MESSAGE_FROM.system,
        content: {
          text: content,
          action: ACTION_TYPE.explainBodyShape,
          data: {
            body_shape: type,
          },
        },
        kind: MESSAGE_TYPE.action,
      });
      this.messages.push({ ...msg, msg_id: uuidv4() });
      this.socket.send(json);
      this.typing.isShow = true;
    },
    initChatSocket() {
      return new Promise(async resolve => {
        const {
          data: { jwt_token: jwtToken },
        } = await $api.user.post(`/api/v1/users/jwt_token`);
        this.socket = new ChatWebSocket({ jwtToken });

        this.socket.on(CHAT_SOCKET_EVENTS.connect, event => {
          this.isSocketConnected = true;
        });
        this.socket.on(CHAT_SOCKET_EVENTS.message, event => {
          this.onSocketNewMessage(event.data);
        });
        this.socket.on(CHAT_SOCKET_EVENTS.close, event => {
          this.socket.off(CHAT_SOCKET_EVENTS.connect);
          this.socket.off(CHAT_SOCKET_EVENTS.message);
          this.socket.off(CHAT_SOCKET_EVENTS.close);
          this.isSocketConnected = false;

          this.initChatSocket();
        });
        resolve();
      });
    },
  },
};
</script>
<style lang="scss">
body {
  overscroll-behavior: none;
}
</style>
<style lang="scss" scoped>
@import '@/scss/animation.scss';

@mixin hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
}

.chat {
  @apply flex flex-col justify-center mx-auto w-full h-full;

  font-family: Poppins;

  *,
  ::v-deep * {
    @include hide-scrollbar;
  }

  .body-container {
    @apply flex-1;
  }

  .question-textarea {
    @apply flex-1 justify-center items-start py-2 pr-2 pl-4 text-base leading-6 text-white rounded;

    border-radius: 4px;
    background: var(--input-bg, rgba(191, 191, 191, 0.25));
    padding-top: 12px;
    resize: none;
    outline: none;

    &::placeholder {
      color: rgba(#fff, 0.3);
    }
  }

  .whitespace-nowrap {
    white-space: nowrap;
  }

  .shortcut-button {
    font-size: 12px;
    border-radius: 38px;
    background-color: var(--tag-bg, #313131);
    border: none;
  }

  .send-button {
    border-radius: 4px;
    outline: none;
    background-color: var(--primary-color);
    transition: opacity 0.3s;

    &:active {
      background-color: var(--button-active-bg);
    }

    &[disabled] {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  .menu-icon {
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
